/**
 * Pronunciation-score normalisation (EAA-1754/1755, shared in EAA-1857).
 *
 * The agent `/api/v3/scoring/speaking/speechace` result varies by engine
 * (ELSA scores 0-5, Speechace 0-100) and nests per-word feedback under `details`
 * in engine-specific shapes. These helpers normalise both into a stable
 * `PronunciationScore` a player can render: an overall 0-100 percentage plus optional
 * per-word quality buckets. Everything is best-effort and tolerant of missing or
 * unrecognised fields — the overall score is the reliable signal.
 *
 * EAA-1857: this lived in `content-type-interactive-reading` as `grading.ts`, which
 * meant Speaking had its own flat `overall_score` lookup and so never applied the
 * Speechace-vs-ELSA scale rule — a Speechace result scoring 80/100 was read as an
 * ELSA 80 and clamped to 100. It sits beside `SpeakingScoreResult` now because it is
 * that response's shape adapter, and both content types share it.
 */

import type { SpeakingScoreResult } from './speakingScore.service';

export type WordScoreBucket = 'good' | 'ok' | 'bad';

/** One graded phoneme within a word (drives the hover detail). */
export interface PronunciationPhoneScore {
    /** Expected phone (engine notation, e.g. "aa"). */
    phone: string;
    /** Normalised 0-100 quality for this phone. */
    quality: number;
    /** What the learner actually sounded like, when the engine reports it. */
    soundLike?: string;
}

/** One graded word in reading order. */
export interface PronunciationWordScore {
    word: string;
    /** Normalised 0-100 quality. */
    quality: number;
    bucket: WordScoreBucket;
    /** Per-phone breakdown (empty when the engine gave none). */
    phones: PronunciationPhoneScore[];
}

export interface PronunciationScore {
    /** Overall pronunciation score as a 0-100 percentage. */
    overall: number;
    /** True when `overall` meets the passing threshold. */
    passed: boolean;
    /** Per-word feedback in reading order (empty when the engine gave none). */
    words: PronunciationWordScore[];
}

/** Quality thresholds for the colour buckets (0-100). */
const GOOD_MIN = 80;
const OK_MIN = 50;

function toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        if (Number.isFinite(n)) return n;
    }
    return null;
}

/** Clamp/round a value already on a 0-100 scale. */
function clamp100(raw: number): number {
    return Math.max(0, Math.min(100, Math.round(raw)));
}

/** Map an ELSA-style 0-5 score to a 0-100 percentage. */
function toPercent(raw: number): number {
    const pct = raw <= 5 ? raw * 20 : raw;
    return clamp100(pct);
}

/**
 * Speechace results already report pronunciation on a 0-100 scale and carry
 * CEFR/PTE/IELTS/TOEIC sub-scores under `text_score` that ELSA does not. Use that
 * shape to decide whether the overall value needs the ELSA 0-5 → ×20 mapping.
 */
function isSpeechaceResult(details: Record<string, unknown> | undefined): boolean {
    const ts = details?.['text_score'];
    if (!ts || typeof ts !== 'object') return false;
    const t = ts as Record<string, unknown>;
    return 'pte_score' in t || 'ielts_score' in t || 'toeic_score' in t || 'cefr_score' in t;
}

function bucketFor(quality: number): WordScoreBucket {
    if (quality >= GOOD_MIN) return 'good';
    if (quality >= OK_MIN) return 'ok';
    return 'bad';
}

/** Locate a per-word score array in the engine's `details`, whatever the shape. */
function findWordList(details: Record<string, unknown> | undefined): unknown[] {
    if (!details) return [];
    const candidates: unknown[] = [
        details['word_score_list'],
        (details['text_score'] as Record<string, unknown> | undefined)?.['word_score_list'],
        details['words'],
        (details['text_score'] as Record<string, unknown> | undefined)?.['words'],
    ];
    for (const c of candidates) {
        if (Array.isArray(c) && c.length > 0) return c;
    }
    return [];
}

/** Read a word's quality (0-100) from the various engine field names. */
function wordQuality(item: Record<string, unknown>): number | null {
    const raw =
        toNumber(item['quality_score']) ??
        toNumber(item['quality']) ??
        toNumber(item['score']) ??
        toNumber(item['pronunciation']);
    if (raw === null) return null;
    // Per-word quality_score is already a 0-100 scale on both engines. Negative
    // values (e.g. -99900) mark a word the learner didn't attempt → clamp to 0.
    return Math.max(0, Math.min(100, Math.round(raw)));
}

/** Extract the per-phone breakdown for the hover detail. */
function wordPhones(item: Record<string, unknown>): PronunciationPhoneScore[] {
    const list = item['phone_score_list'];
    if (!Array.isArray(list)) return [];
    const phones: PronunciationPhoneScore[] = [];
    for (const entry of list) {
        if (!entry || typeof entry !== 'object') continue;
        const p = entry as Record<string, unknown>;
        const phone = typeof p['phone'] === 'string' ? (p['phone'] as string) : '';
        if (!phone) continue; // skip the empty-phone "not attempted" sentinel
        const q = toNumber(p['quality_score']);
        const soundLike =
            typeof p['sound_most_like'] === 'string' ? (p['sound_most_like'] as string) : undefined;
        phones.push({
            phone,
            quality: q != null && q >= 0 ? Math.min(100, Math.round(q)) : 0,
            soundLike,
        });
    }
    return phones;
}

/**
 * Normalise a raw scoring result into a `PronunciationScore`.
 *
 * @param raw - the agent result stored on the aihub job
 * @param passingThreshold - 0-1 pass mark (defaults to 0.7)
 */
export function normalizePronunciationScore(
    input: SpeakingScoreResult | Record<string, unknown> | undefined | null,
    passingThreshold = 0.7
): PronunciationScore {
    if (!input) return { overall: 0, passed: false, words: [] };
    // Accept both the live result and the persisted `scoreResult` record.
    const raw = input as SpeakingScoreResult;

    const rawScore = toNumber(raw.pronunciation) ?? toNumber(raw.score) ?? 0;
    // Speechace reports 0-100 already; only ELSA's 0-5 needs the ×20 mapping.
    const overall = isSpeechaceResult(raw.details) ? clamp100(rawScore) : toPercent(rawScore);

    const words: PronunciationWordScore[] = [];
    for (const entry of findWordList(raw.details)) {
        if (!entry || typeof entry !== 'object') continue;
        const item = entry as Record<string, unknown>;
        const word = typeof item['word'] === 'string' ? (item['word'] as string) : '';
        if (!word) continue;
        const quality = wordQuality(item);
        if (quality === null) continue;
        words.push({ word, quality, bucket: bucketFor(quality), phones: wordPhones(item) });
    }

    return {
        overall,
        passed: overall / 100 >= passingThreshold,
        words,
    };
}
